import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuth, getUser, isAuthenticated } from "./../../auth/auth";
import { useSelectedDictionary } from "../../context/selectedContext.tsx";
import ChangePasswordModal from "../../modal/changePassword/changePassword.tsx";
import NavigationDropdown from "./navigationDropdown/navigationDropdown.tsx";
import type { UserModel } from "../../type/user.tsx";
import { changePasswordIcon, flashCard, logoutIcon, messageIcon, notebookIcon, settingsIcon } from "../../utils/config.tsx";
import UserAvatar from "../../components/userAvatar/userAvatar.tsx";
import { UserPackageBadge } from "../../components/userPackageBadge/userPackageBadge.tsx";
import MessageModal from "../../modal/messageModal/messageModal.tsx";
import "./header.scss";

const TopBar: React.FC = () => {
  const nav = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileNavDropdownRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileNavDropdown, setShowMobileNavDropdown] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [user, setUser] = useState<UserModel | null>(null); 
  const {selected, setSelected} = useSelectedDictionary();
  const [showMessage, setShowMessage] = useState(false);
  const isWords = window.location.pathname === "/words";
  const isRoot = window.location.pathname === "/dictionaries";

  const getDisplayTitle = () => {
    const dictionaryName = selected?.name ? ` / ${selected.name}` : "";
    
    switch (window.location.pathname) {
      case "/settings":
        return "Settings";
      case "/flashcards": {
        const flashcardName = new URLSearchParams(window.location.search).get("name");
        return `Flashcards${flashcardName ? ` / ${flashcardName}` : dictionaryName}`;
      }
      case "/notebook":
        return `Notebook`;
      case "/words":
        return selected?.name ? `${selected.name}` : "All Expressions";  
      default:
        return selected?.name ?? "Select dictionary";
    }
  };

  const displayTitle = getDisplayTitle();

  const openMessage = () => {
    setShowMessage(true);
    setShowDropdown(false);
  };

  const closeMessage = () => {
    setShowMessage(false);
  };

  const handleLogout = () => {
    clearAuth();
    nav("/login");
  };

  const handleGoHome = () => {
    nav("/dictionaries");
  };  

  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      //setParameters(userData?.parameters ?? {});
      setUser(userData);
    }
  }, []);

  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const toggleMobileNavDropdown = () => setShowMobileNavDropdown(!showMobileNavDropdown);

  const openSettings = () => {
    setSelected(null);
    nav("/settings");
    setShowDropdown(false);
  };

  const openChangePassword = () => {
    setShowChangePassword(true);
    setShowDropdown(false);
  };

  const closeChangePassword = () => {
    setShowChangePassword(false);
  };

  const handleNavigateNotebook = () => {
    setSelected(null);
    nav("/notebook");
    setShowMobileNavDropdown(false);
  };

  const handleNavigateFlashcards = () => {
    setSelected(null);
    nav("/flashcards?iddictionary=" + selected?.id + "&name=" + selected?.name);
    setShowMobileNavDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (mobileNavDropdownRef.current && !mobileNavDropdownRef.current.contains(event.target as Node)) {
        setShowMobileNavDropdown(false);
      }
    };
    if (showDropdown || showMobileNavDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showMobileNavDropdown]);

  return (
    <>
      <div className="header">
        <div className="header-left">
          {displayTitle && !isRoot && (
            <>
              {/* Home button */}
              <button
                onClick={handleGoHome}
                className="btn btn-link p-0 me-2"
                style={{ textDecoration: "none" }}
                title="Home (dictionaries)"
                aria-label="Home (dictionaries)"
              >
                <i className="bi bi-house-door" style={{ fontSize: "1.25rem" }}></i>
              </button>
            </>
          )}
          <NavigationDropdown displayTitle={displayTitle} />
        </div>

        <div className="header-right">
          {/* Mobile navigation dropdown */}
          <div className="header-mobile-nav-container" ref={mobileNavDropdownRef}>
            <button 
              className="header-mobile-nav-btn"
              onClick={toggleMobileNavDropdown}
              title="Navigation menu"
              aria-label="Navigation menu"
            >
              <i className="bi bi-list" style={{ fontSize: "1.5rem" }}></i>
            </button>

            {showMobileNavDropdown && (
              <div className="header-mobile-nav-dropdown">
                <button 
                  className="header-mobile-nav-dropdown-item"
                  onClick={handleNavigateNotebook}
                >
                  {notebookIcon} Notebook
                </button>

                {isWords && user?.parameters?.flashcards_visible?.value && (
                  <button 
                    className="header-mobile-nav-dropdown-item"
                    onClick={handleNavigateFlashcards}
                  >
                    {flashCard} Flashcards
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Desktop navigation buttons */}
          <button 
            className="header-right-menu-btn header-desktop-nav-btn"
            onClick={() => { setSelected(null); nav("/notebook"); }}
            title="Go to notebook"
            aria-label="Go to notebook"
          >
            {notebookIcon} Notebook
          </button>

          {isWords && user?.parameters?.flashcards_visible?.value && (
            <button 
              className="header-right-menu-btn header-desktop-nav-btn"
              onClick={handleNavigateFlashcards}
              title="Go to flashcards"
              aria-label="Go to flashcards"
            >
              {flashCard} Flashcards
            </button>
          )}

          <div className="header-user-container" ref={dropdownRef}>
            <button className="header-right-menu-btn" onClick={toggleDropdown} aria-label="User menu">
              <UserAvatar avatar={user?.avatar} username={user?.username} />
              <span>{user?.username}</span>
            </button>

            {showDropdown && (
              <div className="header-user-dropdown">
                <div className="header-user-dropdown-title">
                  <UserAvatar avatar={user?.avatar} username={user?.username} />
                  <div className="header-user-name">{user?.username ?? "User"}</div>
                  <div className="header-user-email">{user?.email ?? "No email"}</div>
                  <UserPackageBadge packageName={user?.package_name} />
                </div>
                <div className="header-dropdown-divider"></div>

                <div className="header-dropdown-divider"></div>
                
                <button className="header-user-dropdown-item" onClick={openSettings}>
                  {settingsIcon} Settings
                </button>                
                <button className="header-user-dropdown-item" onClick={openChangePassword}>
                  {changePasswordIcon} Change password
                </button>
                <button className="header-user-dropdown-item" onClick={openMessage}>
                  {messageIcon} Share your opinion
                </button>                
                <button className="header-user-dropdown-item" onClick={handleLogout}>
                  {logoutIcon} Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Separate modal component */}
      <ChangePasswordModal 
        show={showChangePassword} 
        onClose={closeChangePassword} 
        iduser={user?.iduser ?? 0} 
        isAdmin={user?.admin ?? false}
      />

      <MessageModal
        show={showMessage}
        onHide={closeMessage}
        iduser={user?.iduser ?? 0}
        email={user?.email ?? "No email"}
      />      
    </>
  );
};

export default TopBar;